import { effectiveActRsrcCount, type ObjectOccurrence } from "../data.ingestion.service";
import { ResourceService } from "../../../services/resource.service";
import { SpaceService } from "../../../services/space.service";
import type {
    AvailabilityRequest,
    Conflicts,
    RsConflict,
    RsReservation,
    SpConflict,
    SpReservation,
    S25WsSpace,
} from "../../../services/space.service";
import { S25Util } from "../../../util/s25-util";

export interface LocationSearchResult {
    locationId: number;
    locationName: string;
    availableCount: number;
    availableOccurrenceUuids: string[];
}

export interface ResourceSearchResult {
    resourceId: number;
    resourceName: string;
    availableCount: number;
    availableOccurrenceUuids: string[];
}

/** Shallow copy for building availability payloads (caller merges candidates onto locationIds / resources). */
export function cloneOccurrenceForAvailabilityCheck(occurrence: ObjectOccurrence): ObjectOccurrence {
    return {
        occurrenceUuid: occurrence.occurrenceUuid,
        startDate: occurrence.startDate,
        endDate: occurrence.endDate,
        locationIds: [...(occurrence.locationIds ?? [])],
        resources: [...(occurrence.resources ?? [])],
        comment: occurrence.comment,
    };
}

/** Deep-copy `resources` rows so quantity edits for availability checks do not mutate live form state. */
export function cloneOccurrencesWithResourceRowsCopied(occurrences: ObjectOccurrence[]): ObjectOccurrence[] {
    return occurrences.map((occurrence) => ({
        ...cloneOccurrenceForAvailabilityCheck(occurrence),
        resources: (occurrence.resources ?? []).map((resourceEntry) => ({
            resourceId: resourceEntry.resourceId,
            actRsrcCount: resourceEntry.actRsrcCount,
        })),
    }));
}

export function buildOccurrencesWithCandidateLocations(
    occurrences: ObjectOccurrence[],
    candidateLocationIds: number[],
): ObjectOccurrence[] {
    return occurrences.map((occurrence) => {
        const next = cloneOccurrenceForAvailabilityCheck(occurrence);
        next.locationIds = [...new Set([...next.locationIds, ...candidateLocationIds])];
        return next;
    });
}

export function buildOccurrencesWithCandidateResources(
    occurrences: ObjectOccurrence[],
    candidateResourceIds: number[],
    candidateQuantity: number,
): ObjectOccurrence[] {
    return occurrences.map((occurrence) => {
        const next = cloneOccurrenceForAvailabilityCheck(occurrence);
        const present = new Set(next.resources.map((resourceEntry) => resourceEntry.resourceId));
        for (const candidateResourceId of candidateResourceIds) {
            if (!present.has(candidateResourceId)) {
                next.resources.push({ resourceId: candidateResourceId, actRsrcCount: candidateQuantity });
                present.add(candidateResourceId);
            }
        }
        return next;
    });
}

function indexSpConflictsByRoom(conflicts: SpConflict[] | undefined): Map<number, SpConflict[]> {
    const byRoomId = new Map<number, SpConflict[]>();
    for (const conflict of conflicts ?? []) {
        const roomId = conflict.origRoomId ?? conflict.roomId;
        if (!byRoomId.has(roomId)) {
            byRoomId.set(roomId, []);
        }
        byRoomId.get(roomId)!.push(conflict);
    }
    return byRoomId;
}

function indexSpReservationsByRoom(reservations: SpReservation[] | undefined): Map<number, SpReservation[]> {
    const byRoomId = new Map<number, SpReservation[]>();
    for (const reservation of reservations ?? []) {
        const roomId = reservation.roomId;
        if (!byRoomId.has(roomId)) {
            byRoomId.set(roomId, []);
        }
        byRoomId.get(roomId)!.push(reservation);
    }
    return byRoomId;
}

function applySpaceAvailabilityFromResponse(
    occurrencesForCheck: ObjectOccurrence[],
    response: { data?: Conflicts; error?: unknown },
    result: Map<string, Set<number>>,
): void {
    if (response.error) {
        return;
    }
    const conflictsByRoomId = indexSpConflictsByRoom(response.data?.spConflicts);
    const reservationsByRoomId = indexSpReservationsByRoom(response.data?.apSpReservations);

    for (const occurrence of occurrencesForCheck) {
        for (const locationId of occurrence.locationIds ?? []) {
            const locationConflicts = conflictsByRoomId.get(locationId) ?? [];
            const locationReservations = reservationsByRoomId.get(locationId) ?? [];
            const hasConflicts = locationConflicts.some(
                (conflict) => conflict.candidateRsrvExtUid === occurrence.occurrenceUuid,
            );
            const blockedByAp = locationReservations.some(
                (reservation) =>
                    reservation.extUid === occurrence.occurrenceUuid &&
                    reservation.apInfo &&
                    !reservation.apInfo.canRequest &&
                    !reservation.apInfo.canAssign,
            );
            if (!hasConflicts && !blockedByAp) {
                result.get(occurrence.occurrenceUuid)!.add(locationId);
            }
        }
    }
}

function indexRsConflictsByResourceId(conflicts: RsConflict[] | undefined): Map<number, RsConflict[]> {
    const byResourceId = new Map<number, RsConflict[]>();
    for (const conflict of conflicts ?? []) {
        const resourceId = conflict.origResourceId ?? conflict.resourceId;
        if (!byResourceId.has(resourceId)) {
            byResourceId.set(resourceId, []);
        }
        byResourceId.get(resourceId)!.push(conflict);
    }
    return byResourceId;
}

function indexRsReservationsByResourceId(reservations: RsReservation[] | undefined): Map<number, RsReservation[]> {
    const byResourceId = new Map<number, RsReservation[]>();
    for (const reservation of reservations ?? []) {
        const resourceId = reservation.resourceId;
        if (!byResourceId.has(resourceId)) {
            byResourceId.set(resourceId, []);
        }
        byResourceId.get(resourceId)!.push(reservation);
    }
    return byResourceId;
}

function applyResourceAvailabilityFromResponse(
    occurrencesForCheck: ObjectOccurrence[],
    response: { data?: Conflicts; error?: unknown },
    result: Map<string, Set<number>>,
): void {
    if (response.error) {
        return;
    }
    const conflictsByResourceId = indexRsConflictsByResourceId(response.data?.rsConflicts);
    const reservationsByResourceId = indexRsReservationsByResourceId(response.data?.apRsReservations);

    for (const occurrence of occurrencesForCheck) {
        for (const resourceEntry of occurrence.resources ?? []) {
            const resourceId = resourceEntry.resourceId;
            const resourceConflicts = conflictsByResourceId.get(resourceId) ?? [];
            const resourceReservations = reservationsByResourceId.get(resourceId) ?? [];
            const hasConflicts = resourceConflicts.some(
                (conflict) => conflict.candidateRsrvExtUid === occurrence.occurrenceUuid,
            );
            const blockedByAp = resourceReservations.some(
                (reservation) =>
                    reservation.extUid === occurrence.occurrenceUuid &&
                    reservation.apInfo &&
                    !reservation.apInfo.canRequest &&
                    !reservation.apInfo.canAssign,
            );
            if (!hasConflicts && !blockedByAp) {
                result.get(occurrence.occurrenceUuid)!.add(resourceId);
            }
        }
    }
}

function availabilityDatesFromOccurrences(occurrencesForCheck: ObjectOccurrence[]) {
    return occurrencesForCheck.map((occurrence) => ({
        start_dt: occurrence.startDate,
        end_dt: occurrence.endDate,
        ext_uid: occurrence.occurrenceUuid,
        space: (occurrence.locationIds ?? []).map((spaceId) => ({ space_id: spaceId })),
        resource: (occurrence.resources ?? []).map((resourceEntry) => ({
            resource_id: resourceEntry.resourceId,
            requested_quantity: effectiveActRsrcCount(resourceEntry),
        })),
    }));
}

/**
 * Single report.json call. `occurrencesForCheck` must already include every space id to evaluate on each row's
 * `locationIds` (caller merges search candidates). Returns occurrenceUuid → set of location ids that pass.
 */
export async function checkOccurrenceLocationAvailability(
    occurrencesForCheck: ObjectOccurrence[],
): Promise<Map<string, Set<number>>> {
    const result = new Map<string, Set<number>>();
    occurrencesForCheck.forEach((occurrence) => result.set(occurrence.occurrenceUuid, new Set()));

    if (occurrencesForCheck.length === 0) {
        return result;
    }

    const request: AvailabilityRequest = {
        availability: {
            event: {
                event_id: -1,
                profile: {
                    profile_id: -1,
                },
            },
            requirements: { allow_sharing: "F" },
            dates: availabilityDatesFromOccurrences(occurrencesForCheck),
        },
    };

    try {
        const response = await SpaceService.checkSpaceAvailability(request, false, false, false);
        if (response.error) {
            console.error("Error checking availability:", response.error);
            return result;
        }
        applySpaceAvailabilityFromResponse(occurrencesForCheck, response, result);
    } catch (caughtError) {
        console.error("Error checking availability:", caughtError);
    }

    return result;
}

/**
 * Single report.json call. `occurrencesForCheck` must already include every resource line to evaluate on each row's
 * `resources` (caller merges search candidates with quantity). Returns occurrenceUuid → set of resource ids that pass.
 */
export async function checkOccurrenceResourceAvailability(
    occurrencesForCheck: ObjectOccurrence[],
): Promise<Map<string, Set<number>>> {
    const result = new Map<string, Set<number>>();
    occurrencesForCheck.forEach((occurrence) => result.set(occurrence.occurrenceUuid, new Set()));

    if (occurrencesForCheck.length === 0) {
        return result;
    }

    const request: AvailabilityRequest = {
        availability: {
            event: {
                event_id: -1,
                profile: {
                    profile_id: -1,
                },
            },
            requirements: { allow_sharing: "F" },
            dates: availabilityDatesFromOccurrences(occurrencesForCheck),
        },
    };

    try {
        const response = await ResourceService.checkAvailabilityReport(request, false, false, false);
        if (response.error) {
            console.error("Error checking availability:", response.error);
            return result;
        }
        applyResourceAvailabilityFromResponse(occurrencesForCheck, response, result);
    } catch (caughtError) {
        console.error("Error checking resource availability:", caughtError);
    }

    return result;
}

export async function fetchLocationResultsFromSearch(
    searchQuery: string,
    occurrences: ObjectOccurrence[],
    existingLocationIds: number[] = [],
): Promise<LocationSearchResult[]> {
    const spacesData = await SpaceService.getSpacesBySearchQuery(searchQuery);
    const spaces = spacesData?.spaces?.space || [];

    if (spaces.length === 0) {
        return [];
    }

    const existing = new Set(existingLocationIds);
    const locationData = spaces
        .map((space: S25WsSpace) => ({
            locationId: parseInt(String(space.space_id)),
            locationName: String(space.space_name || space.formal_name || `Location ${space.space_id}`),
        }))
        .filter((data) => !existing.has(data.locationId));

    if (locationData.length === 0) {
        return [];
    }

    const candidateLocationIds = locationData.map((data) => data.locationId);
    const occurrencesForCheck = buildOccurrencesWithCandidateLocations(occurrences, candidateLocationIds);
    const availabilityMap = await checkOccurrenceLocationAvailability(occurrencesForCheck);

    const results: LocationSearchResult[] = [];
    for (const { locationId, locationName } of locationData) {
        const availableOccurrenceUuids: string[] = [];

        for (const occurrence of occurrences) {
            if (availabilityMap.get(occurrence.occurrenceUuid)?.has(locationId)) {
                availableOccurrenceUuids.push(occurrence.occurrenceUuid);
            }
        }

        if (availableOccurrenceUuids.length > 0) {
            results.push({
                locationId,
                locationName,
                availableCount: availableOccurrenceUuids.length,
                availableOccurrenceUuids,
            });
        }
    }

    results.sort((first, second) => {
        if (second.availableCount !== first.availableCount) {
            return second.availableCount - first.availableCount;
        }
        return first.locationName.toLowerCase().localeCompare(second.locationName.toLowerCase());
    });

    return results;
}

export async function fetchResourceResultsFromSearch(
    searchQuery: string,
    occurrences: ObjectOccurrence[],
    existingResourceIds: number[],
    searchResourceQuantity: number = 1,
): Promise<ResourceSearchResult[]> {
    const quantity = Number.isFinite(searchResourceQuantity) && searchResourceQuantity > 0 ? searchResourceQuantity : 1;

    const data = await ResourceService.getResourcesBySearchQuery(searchQuery, []);
    const raw = S25Util.prettifyJson(data, null, { resource: true }) as {
        resources?: { resource?: unknown[] };
    };
    const arr = S25Util.array.forceArray(raw?.resources?.resource) as Record<string, unknown>[];
    const existing = new Set(existingResourceIds);
    const candidates: { resourceId: number; resourceName: string }[] = [];
    for (const resourceRow of arr) {
        const resourceId = parseInt(String(resourceRow.resource_id ?? resourceRow.resourceId ?? 0), 10);
        const resourceName = String(resourceRow.resource_name ?? resourceRow.resourceName ?? `Resource ${resourceId}`);
        if (resourceId && !existing.has(resourceId)) {
            candidates.push({ resourceId, resourceName });
        }
    }

    if (candidates.length === 0) {
        return [];
    }

    const candidateResourceIds = candidates.map((candidate) => candidate.resourceId);
    const occurrencesForCheck = buildOccurrencesWithCandidateResources(occurrences, candidateResourceIds, quantity);
    const availabilityMap = await checkOccurrenceResourceAvailability(occurrencesForCheck);

    const results: ResourceSearchResult[] = [];
    for (const { resourceId, resourceName } of candidates) {
        const availableOccurrenceUuids: string[] = [];
        for (const occurrence of occurrences) {
            if (availabilityMap.get(occurrence.occurrenceUuid)?.has(resourceId)) {
                availableOccurrenceUuids.push(occurrence.occurrenceUuid);
            }
        }
        if (availableOccurrenceUuids.length > 0) {
            results.push({
                resourceId,
                resourceName,
                availableCount: availableOccurrenceUuids.length,
                availableOccurrenceUuids,
            });
        }
    }

    results.sort((first, second) => {
        if (second.availableCount !== first.availableCount) {
            return second.availableCount - first.availableCount;
        }
        return first.resourceName.toLowerCase().localeCompare(second.resourceName.toLowerCase());
    });

    return results;
}
